import { biliBridge, inBiliApp, isNewJsBridge } from '@bilibili/js-bridge'
import createError from 'axios/lib/core/createError'
import transformData from 'axios/lib/core/transformData'
import defaults from 'axios/lib/defaults'
import pkg from '../../package'
import { convertRequestURL, appendRequestData, stringifyPostData, convertRequestHeader } from './../shim'
import { BRIDGE_METHOD, DEF_CONST, AUTH_METHOD } from './../enum'
import { isAndroid, isArray, isIframe, isFunction, debugFn } from './../utils'
import { getBridgeName, getAuthType, getCsrfKey, getTimeout, getCsrfPos } from './../process'

const createBridge = (option, runtime) => {
  option = option || {}
  if (!inBiliApp || !isNewJsBridge()) {
    return null
  }

  if (isIframe && !isAndroid) {
    return null
  }

  if (!runtime.version) {
    return null
  }

  return function (method, url, data, config) {
    data = data || {}
    config = config || {}

    if (method === DEF_CONST.method.GET && /\?/.test(url)) {
      const arr = url.split('?')
      const obj = {}
      arr[1].split('&').forEach((item) => {
        const kv = item.split('=')
        obj[decodeURIComponent(kv[0])] = decodeURIComponent(kv[1])
      })
      url = arr[0]
      data = {
        ...obj,
        ...data
      }
    }

    if (isFunction(option.onRequest)) {
      const newArgs = option.onRequest({ data, method, url, config })
      if (newArgs) {
        url = newArgs.url || url
        data = newArgs.data || data
        method = newArgs.method || method
        config = newArgs.config || config
      }
    }

    const bridge = getBridgeName(option, config, method, runtime)
    if (!bridge) {
      return null
    }

    return new Promise((resolve, reject) => {
      appendRequestData(data, method, option, config, runtime).then(async (requestData) => {
        const requestHead = await convertRequestHeader(method, option, config, runtime)
        const connectData = {
          method,
          url: convertRequestURL(url, requestData, method, option, config, runtime),
          timeout: getTimeout(option, config) / 1000,
          header: requestHead
        }

        /**
         * net.request 需要传 policy 这个参数
         */
        if (bridge === BRIDGE_METHOD.request) {
          const auth = getAuthType(option, config)
          connectData.policy = auth === AUTH_METHOD.web ? '0' : '1'
        }

        /**
         * 老版本支持传 csrfKey
         */
        const csrfPos = getCsrfPos(option, config)
        if (runtime.version < 6 && csrfPos === DEF_CONST.csrfPos.params) {
          connectData.csrfKey = getCsrfKey(option, config)
        }

        /**
         * net.requestWithSign 的参数需要放在 params 里，其它放在 data 里
         */
        if (bridge === BRIDGE_METHOD.requestWithSign) {
          connectData.params = requestData
        } else if (method === DEF_CONST.method.POST) {
          connectData.data = stringifyPostData(requestData, option, config, runtime)
        }

        debugFn(option, { version: pkg.version, runtime, method: bridge, data: connectData })

        biliBridge.callNative({
          method: bridge,
          data: connectData,
          callback: (status, message) => {
            debugFn(option, { lifecycle: 'callback', value: { status, message } })
            if (status !== 'ok') {
              const err = createError(message || status, { option, config, runtime, bridge }, -1, connectData, null)
              reject(err)
            }
          },
          onLoad: ({ response, httpStatus }) => {
            debugFn(option, { lifecycle: 'onLoad', value: { response, httpStatus } })
            if (httpStatus < 200 || httpStatus >= 400) {
              let err
              try {
                const res = JSON.parse(response)
                err = createError(response, { option, config, runtime, bridge }, httpStatus, connectData, res)
              } catch (e) {
                err = createError(e.message, { option, config, runtime, bridge }, httpStatus, connectData, response)
              }
              return reject(err)
            }

            if (isArray(config.transformResponse)) {
              response = transformData(response, requestHead, config.transformResponse)
            } else if (config.responseType !== 'text') {
              response = transformData(response, requestHead, defaults.transformResponse)
            }

            if (isFunction(option.onSuccess)) {
              response =
                option.onSuccess({
                  url: connectData.url,
                  method: method.toLowerCase(),
                  status: httpStatus,
                  config: {
                    headers: requestHead
                  },
                  data: response
                }) || response
            }

            resolve(response)
          }
        })
      })
    })
  }
}

export default createBridge
